import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { Hide } from '#/components/Hide/Hide';
import { Image } from '#/components/Image/Image';
import { Body } from '#/components/Text/Body';
import { H2 } from '#/components/Text/H2';
import { Tile } from '#/components/Tile/Tile';
import { useElementReachedEffect } from '#/hooks/viewport/useElementReachedEffect';
import { useI18n } from '#/i18n/hooks/useI18n';
import {
  authLoginRoute,
  authSignupRoute,
} from '#/modules/auth/routing/auth.routes';
import type { ListingType } from '#/types/ListingType';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { trackEventOnce } from '#/utils/analytics/track/trackEventOnce';
import { LISTING_TYPE_HUMAN_TRACKING_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { getStaticImageSrc } from '#/utils/getImageSrc';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  listingUrl: string;
  type: ListingType;
};

export function ListingViewSignupCta({ listingUrl, type }: Props) {
  const { i18n } = useI18n();
  const elementReachedRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const authRedirectPath = `${location.pathname}${location.search}`;

  useElementReachedEffect({
    ref: elementReachedRef,
    threshold: 0.5,
    onReach: useCallback(() => {
      trackEventOnce('Displayed: Job Listing CTA block');
    }, []),
  });

  return (
    <div data-qa-id="job-listing-signup-promo" ref={elementReachedRef}>
      <Tile
        $gradientBorder
        position="relative"
        display="flex"
        flex="1"
        py={5}
        px={4}
        borderRadius="4px"
      >
        <Box display="flex" justifyContent="center" width={[0, 0, '50%']}>
          <Hide xs sm>
            <Image
              src={getStaticImageSrc('planning.svg')}
              alt="Illustration"
              expandSize
            />
          </Hide>
        </Box>
        <Box
          textAlign={['center', 'center', 'left']}
          width={['100%', '100%', '50%']}
        >
          <H2 mb={3}>
            {type === 'JOB'
              ? i18n('Take the Next Step in Your Career')
              : i18n('Discover Your Calling')}
          </H2>
          <Body mb={3}>
            {type === 'JOB'
              ? i18n(
                  'Match with social-impact hiring managers, explore the latest job opportunities, and get notified when new opportunities meet your search criteria.',
                )
              : i18n(
                  'Find opportunities to change the world with the latest social-impact job, internship, and volunteer listings. Plus, explore resources for taking action in your community.',
                )}
          </Body>

          <Box mb={3}>
            <Button
              type="link"
              href={authSignupRoute.with({
                to: authRedirectPath,
              })}
              anchorType="native"
              variant="primary"
              onClick={(e) => {
                e.preventDefault();
                trackClicked(
                  `${LISTING_TYPE_HUMAN_TRACKING_NAME_MAP_TITLE[type]} Listing CTA: Sign Up`,
                );
                showModal('SIGNUP', {
                  redirectTo: listingUrl,
                });
              }}
              qaId="listing-promo-signup-link"
            >
              {i18n('Sign Up Now')}
            </Button>
          </Box>

          <Button
            type="link"
            href={authLoginRoute.with({ to: authRedirectPath })}
            variant="link"
            size="small"
            anchorType="native"
            noPadding
            onClick={(e) => {
              e.preventDefault();
              trackClicked(
                `${LISTING_TYPE_HUMAN_TRACKING_NAME_MAP_TITLE[type]} Listing CTA: Log In`,
              );
              showModal('LOGIN', {
                redirectTo: listingUrl,
              });
            }}
            qaId="listing-promo-login-link"
          >
            {i18n('Already a user? Log in')}
          </Button>
        </Box>
      </Tile>
    </div>
  );
}
